import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AppRoutes } from '@client/routes/routes'
import { useProofOfWork } from '@client/components'
import { Utils as utils } from '@client/utility'
import { useUserStore } from '@client/store'
import { Box } from '@mui/material'
import { BlockedPage } from '../client/routes/routing'
import { HelmetProvider } from 'react-helmet-async'
import { HelmetWrapper } from './helmetWrapper'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import * as Sentry from '@sentry/react'
import { ErrorBoundary } from '@client/routes/errorBoundary'
import { SUPABASE_ANON_KEY_OLD } from './config'

const initialOptions = {
  clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID,
  intent: 'capture', 
  components: 'buttons',
  vault: false 
}

Sentry.init({
  dsn: 'https://2bed6b35dd70e8068f61a53812a8a5fc@o4509146215284736.ingest.us.sentry.io/4509243214725120 ',
  sendDefaultPii: true,
  integrations: [Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})

const AppContent = () => {
  const [isBlocked, setIsBlocked] = useState(false)
  const [publicIP, setPublicIP] = useState<string>('unknown')
  const setIsDeleted = useUserStore((state) => state.setIsDeleted)
  const setIsBanned = useUserStore((state) => state.setIsBanned)
  const setToken = useUserStore((state) => state.setToken)
  const token = useUserStore((state) => state.token)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    let isMounted = true

    const initChecks = async () => {
      if (location.pathname === '/Blocked' || isBlocked) return

      try {
        if (!token) {
          const { data } = await utils.db.client.auth.getSession()
          const newToken = data?.session?.access_token
          if (newToken) setToken(newToken)
        }

        const user = await utils.db.getUser()
        const userId = user?.id

        const res = await fetch('https://wetxjqppusuquqtzhvfv.supabase.co/functions/v1/geocheck ', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${SUPABASE_ANON_KEY_OLD}`,
          },
          body: JSON.stringify(userId ? { user_uuid: userId } : {})
        })
        const geoData: any = await res.json()

        if (!geoData?.allowed) {
          if (!isMounted) return
          setIsBlocked(true)
          if (location.pathname !== '/Blocked') navigate('/Blocked', { replace: true })
          return
        }

        if (geoData?.public_ip) setPublicIP(geoData.public_ip)

        if (userId && geoData) {
          const isDeleted = geoData.is_deleted === true || geoData.is_deleted === 'true'
          const isBanned = geoData.is_banned === true || geoData.is_banned === 'true'

          if (isDeleted) {
            setIsDeleted(true)
            await utils.db.client.auth.signOut()
            navigate('/Blocked?type=deleted', { replace: true })
            return
          }

          if (isBanned) {
            setIsBanned(true)
            await utils.db.client.auth.signOut()
            navigate('/Blocked?type=banned', { replace: true })
            return
          }

          setIsDeleted(false)
          setIsBanned(false)
        }
      } catch (err) {
        Sentry.captureException(err)
        setIsDeleted(false)
        setIsBanned(false)
      }
    }

    initChecks()
    return () => { isMounted = false }
  }, [location, navigate, setIsDeleted, setIsBanned, setToken, token, isBlocked])

  useProofOfWork(() => {}, { public_ip: publicIP })
  
  if (isBlocked) return <BlockedPage />

  const activeTheme = document.documentElement.getAttribute('data-mui-theme')

  return (
    <PayPalScriptProvider options={initialOptions}>
      <HelmetWrapper>
        <Box
          data-mui-theme={activeTheme || 'theme-dark'}
          aria-label="PyrenzAI"
          sx={{
            scrollBehavior: 'smooth',
            scrollbarWidth: 'thin',
            scrollbarColor: 'transparent transparent',
            '&::-webkit-scrollbar': { width: 8, opacity: 0.2, transition: 'opacity 0.3s ease' },
            '&:hover::-webkit-scrollbar': { opacity: 1 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(100, 100, 100, 0.4)',
              borderRadius: 10,
              border: '2px solid transparent',
              backgroundClip: 'content-box'
            }
          }}
        >
          <Routes>{AppRoutes}</Routes>
        </Box>
      </HelmetWrapper>
    </PayPalScriptProvider>
  )
}

export function App() {
  return (
    <HelmetProvider>
      <Router>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  )
}